import {
  getCreditLineForGiver,
  getCreditLinesForGiver,
  getOwnCompanyProducts,
} from 'lib/api';
import GlobalSearcher, { Filter } from 'lib/common/GlobalSearcher';
import { PRODUCT_TYPES } from 'lib/consts';
import { CREDIT_LINE_STATES } from 'lib/consts';
import CenteredCircularProgress from 'lib/designSystem/CircularProgress';
import HeaderWithActions, {
  CustomButtonProps,
} from 'lib/designSystem/HeaderWithActions';
import TableWithPagination from 'lib/designSystem/TableWithPagination';
import { hasAnyPermissions, validatePermissions } from 'lib/functions/utils';
import { Product } from 'lib/types/models';
import { Pagination } from 'lib/types/shared';
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { AuthState } from 'services/redux/authSlice';

import CreateClientCreditLineDialog from './components/createClientCreditLine';
import CreateStakeholderCreditLineDialog from './components/createStakeholderCreditLine';
import SelectCreditLineType from './components/selectCreditLineType';
import ShoppingDialogRecieved from './components/ShoppingDialog';

const BASE_PAGINATION = { page: 1, pageSize: 25, total: 0, totalPages: 0 };
const BIG_PAGE_SIZE = 10000;

export interface CreditLine {
  status: string;
  companyReceiverId: number;
  productId: number;
  productName: string;
  description?: string;
}

interface FilterOption {
  value: string;
  label: string;
}

const CreditLineRequested: React.FC = () => {
  const user = useSelector((state: { auth: AuthState }) => state.auth.user);
  const dispatch = useDispatch();

  // Main state
  const [creditLines, setCreditLines] = useState<CreditLine[]>([]);
  const [productOptions, setProductOptions] = useState<FilterOption[]>([]);
  const [loading, setLoading] = useState(false);
  const [miniLoading, setMiniLoading] = useState(false);
  const [isSgr, setIsSgr] = useState(false);

  // Dialog states
  const [shoppingCartType, setShoppingCartType] = useState('');
  const [openShoppingCart, setOpenShoppingCart] = useState(false);
  const [openRequest, setOpenRequest] = useState<CreditLine | null>(null);
  const [openClientDialog, setOpenClientDialog] = useState(false);
  const [openStakeholderDialog, setOpenStakeholderDialog] = useState(false);
  const [openSelectCreditLineType, setOpenSelectCreditLineType] =
    useState(false);

  // Filter states
  const [creditLineId, setCreditLineId] = useState<number | null>(null);
  const [businessName, setBusinessName] = useState<string | null>(null);
  const [executive, setExecutive] = useState<string | null>(null);
  const [status, setStatus] = useState<FilterOption[]>([]);
  const [productName, setProductName] = useState<string | null>(null);
  const [sortBy, setSortBy] = useState<string>('createdAt');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
  const [pagination, setPagination] = useState<Pagination>(BASE_PAGINATION);

  const getDataHeaders = () => {
    const baseHeaders = [
      { key: 'id', label: 'ID', type: 'numeric-id' },
      { key: 'businessName', label: 'Cliente' },
      { key: 'productName', label: 'Producto' },
      { key: 'amount', label: 'Monto', type: 'money' },
      { key: 'amountUsed', label: 'Monto Utilizado', type: 'money' },
      { key: 'minutesSinceUpdate', label: 'Estancado', type: 'formatMinutes' },
      { key: 'validUntil', label: 'Vencimiento', type: 'date' },
      { key: 'createdAt', label: 'Creado en', type: 'date' },
      { key: 'status', label: 'Estado', type: 'status' },
    ];

    if (validatePermissions(user, 'businesses_read_all')) {
      baseHeaders.push({
        key: 'executives',
        label: 'Ejecutivos',
        type: 'array-names',
      });
    }
    return baseHeaders;
  };

  const generateQuery = ({
    page,
    pageSize,
  }: {
    page: number;
    pageSize: number;
  }) => {
    const params = [
      creditLineId && `creditLineId=${creditLineId}`,
      businessName && `businessName=${businessName}`,
      executive && `executive=${executive}`,
      status.length > 0 && `status=${status.map((s) => s.value).join(',')}`,
      productName && `productName=${productName}`,
      sortBy && `sortBy=${sortBy}`,
      sortOrder && `sortOrder=${sortOrder}`,
      page && `page=${page}&pageSize=${pageSize}`,
    ].filter(Boolean);

    return params.join('&');
  };

  const handleDetails = async (id: number) => {
    setMiniLoading(true);
    try {
      setOpenClientDialog(false);
      const request = await getCreditLineForGiver(dispatch, { id });
      setOpenRequest(request);
      setShoppingCartType(request.status);
      setOpenShoppingCart(true);
    } catch (error) {
      alert('error');
    } finally {
      setMiniLoading(false);
    }
  };

  const fetchCreditLines = async (params?: {
    page: number;
    pageSize: number;
    exportComplete?: boolean;
  }) => {
    try {
      if (params?.exportComplete) {
        const query = generateQuery({ page: 1, pageSize: BIG_PAGE_SIZE });
        const response = await getCreditLinesForGiver(dispatch, query);
        return response;
      } else {
        setLoading(true);
        const query = generateQuery({
          page: params?.page ?? pagination.page,
          pageSize: params?.pageSize ?? pagination.pageSize,
        });
        const response = await getCreditLinesForGiver(dispatch, query);
        setCreditLines(response.data || []);
        setPagination(response.pagination);
      }
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const fetchCompanyProducts = async () => {
    try {
      const availableProducts = await getOwnCompanyProducts(dispatch);
      const productIds = availableProducts.map(
        (product: Product) => product.typeId,
      );
      const mappedProductOptions = [
        { value: '', label: 'Todos' },
        ...PRODUCT_TYPES.filter((productType) =>
          productIds.includes(productType.value),
        ),
      ];
      setIsSgr(availableProducts.some((x: any) => x.typeId === 'sgr'));
      setProductOptions(mappedProductOptions);
    } catch (error) {
      console.error(error);
      setProductOptions([{ value: '', label: 'Todos' }, ...PRODUCT_TYPES]);
    }
  };

  const handleRequest = async () => {
    const products = user?.products?.map((product) => product.typeId);
    if (products?.includes('factoring')) {
      setOpenSelectCreditLineType(true);
    } else {
      setOpenClientDialog(true);
    }
  };

  const filters: Filter[] = [
    {
      type: 'text' as const,
      label: 'ID Linea',
      value: creditLineId,
      setter: setCreditLineId,
    },
    {
      type: 'autocomplete' as const,
      label: 'Nombre Cliente',
      key: 'businessName',
      value: businessName,
      setter: setBusinessName,
    },
    ...(validatePermissions(user, 'businesses_read_all')
      ? [
          {
            label: 'Ejecutivos',
            type: 'autocomplete' as const,
            key: 'userName',
            value: executive,
            setter: setExecutive,
            options: [],
          },
        ]
      : []),
    {
      type: 'multiple' as const,
      label: 'Estado',
      value: status,
      setter: setStatus,
      options: CREDIT_LINE_STATES,
    },
    {
      type: 'select' as const,
      label: 'Nombre Producto',
      value: productName,
      setter: setProductName,
      options: productOptions,
    },
  ];

  const buttonsData: CustomButtonProps[] = [
    {
      name: 'Filtro',
      action: () => {},
      color: 'primary',
      customButton: (
        <GlobalSearcher
          filters={filters}
          triggerSearch={fetchCreditLines}
          selectedSortBy={sortBy}
          setSelectedSortBy={setSortBy}
          sortOptions={[{ value: 'createdAt', label: 'Creación' }]}
          sortOrder={sortOrder}
          setSortOrder={setSortOrder}
          dispatch={dispatch}
        />
      ),
    },
    {
      action: () => void handleRequest(),
      name: 'Solicitar',
      color: 'primary',
      disabled: !hasAnyPermissions(user, ['financing_creditLine_create']),
    },
  ];

  useEffect(() => {
    void fetchCreditLines({
      page: pagination.page,
      pageSize: pagination.pageSize,
    });
    void fetchCompanyProducts();
  }, []);

  if (loading) return <CenteredCircularProgress />;

  return (
    <div>
      <HeaderWithActions
        title="Acá verás todas las líneas de crédito que se han solicitado"
        variant="body1"
        buttons={buttonsData}
      />
      {miniLoading && <CenteredCircularProgress type="layout" />}
      <TableWithPagination
        data={creditLines}
        setData={setCreditLines}
        dataHeaders={getDataHeaders()}
        handleClickRow={handleDetails}
        showActions
        pagination={pagination}
        setPagination={setPagination}
        searchData={fetchCreditLines}
        setLoading={setLoading}
        exportToFile="xlsx"
        exportComplete
        lastElement
      />
      <ShoppingDialogRecieved
        shoppingCartType={shoppingCartType}
        openShoppingCart={openShoppingCart}
        setOpenShoppingCart={setOpenShoppingCart}
        openRequest={openRequest}
        setSelectedRequest={setOpenRequest}
        setUp={fetchCreditLines}
        handleDetails={handleDetails}
      />
      {openClientDialog && (
        <CreateClientCreditLineDialog
          open={openClientDialog}
          setOpen={setOpenClientDialog}
          handleDetails={handleDetails}
          setUp={fetchCreditLines}
        />
      )}
      {openStakeholderDialog && (
        <CreateStakeholderCreditLineDialog
          open={openStakeholderDialog}
          setOpen={setOpenStakeholderDialog}
          handleDetails={handleDetails}
          setUp={fetchCreditLines}
        />
      )}
      {openSelectCreditLineType && (
        <SelectCreditLineType
          open={openSelectCreditLineType}
          setOpen={setOpenSelectCreditLineType}
          setOpenClientDialog={setOpenClientDialog}
          setOpenStakeholderDialog={setOpenStakeholderDialog}
        />
      )}
    </div>
  );
};

export default CreditLineRequested;
